/**
 * @file src/sync/tempoRange.js
 * @description The transport tempo's real range — the ONE place these numbers live.
 *
 * The deck clamps to it (`Deck.setTempo`), the BPM number is registered with it, and the header
 * input declares it. Before this module the range lived only on the `sync-bpm` PARAM while the deck
 * itself was unbounded, so a tempo fader could drive a deck to 3 BPM while the readout sat at its
 * old floor of 20 — the number on screen was not the number playing.
 *
 * It is deliberately wide. The old 20–300 was a UI range with no engine behind it; what actually
 * limits a deck is its sink's playback RATE (the streaming backend throws outside 1/16..16×, the
 * stretch engine floors at 0.01), and that limit is per track, not a global BPM. So the transport
 * is free across the range a musician might ask for, and a fader that asks for more than this range
 * can hold keeps the remainder on its own deck instead (see `driveResidual` in the tempoPitch
 * factory) — which is also what makes the speed-and-reverse stop a real stop.
 *
 * Zero is the one value that is genuinely impossible: a 0 BPM transport has no beat interval
 * (60/0), so every grid, metronome and quantized start built on it would hang.
 */

/** Lowest tempo the transport can hold. Not 0 — a 0 BPM grid has no beat. */
export const TEMPO_MIN_BPM = 1;

/** Highest tempo the transport can hold. */
export const TEMPO_MAX_BPM = 999;

/**
 * Clamp a proposed tempo into the transport's range.
 * @param {number} bpm
 * @returns {number|null} the clamped tempo, or null when the input is not a usable number.
 */
export function clampTempo(bpm) {
  const numeric = Number(bpm);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  if (numeric < TEMPO_MIN_BPM) return TEMPO_MIN_BPM;
  if (numeric > TEMPO_MAX_BPM) return TEMPO_MAX_BPM;
  return numeric;
}
